/**
 * Vehicle Manager for FlowRide Frontend
 * Manages real-time vehicle tracking and visualization on the map.
 * Handles vehicle data, caching, and rendering optimization.
 */

import { type Map as MapLibre } from "maplibre-gl";
import {
    removeBusRouteShapesFromMap,
    syncBusRouteShapesOnMap,
} from "../components/MapLibre/mapDebugLayers";
import { VEHICLE } from "../config/global.configuration";
import { InterpolationEngine, type VehicleRenderPosition } from "../engine/InterpolationEngine";
import { type Color } from "../model/AtlasConfig";
import { type CompactTimeline } from "../model/realtime/CompactTimeline";
import { RealtimeCache } from "../model/RealtimeCache";
import { Vehicle } from "../model/Vehicle";
import { VehicleType } from "../model/VehicleType";
import {
    getTimelineLeadingCoordAtMs,
    timelineEpochTimeWindowMs,
} from "../tools/compactTimelineDisplay.tools";
import type { TripDelayWire, TripScheduleWire } from "../types/dtos/realtime-api/realtime.dto";

export class VehicleManager {

    /*
     * ---------------------------------------------------------------------
     * Main dictionary: map vehicle.id -> Vehicle
     * ---------------------------------------------------------------------
     */
    private readonly vehicles!: Map<string, Vehicle>;

    // Caches to avoid repeated conversions (colors & types)
    private readonly colorCache!: Map<string, Color>;

    private readonly typeCache!: Map<string, VehicleType>;


    /** Visibility state of all vehicles */
    private visible = true;

    // MapLibre map instance (can be undefined if not yet set)
    private map?: MapLibre;


    private realtimeCache: RealtimeCache | null = null;

    /**
     * Per-trip delay overrides set by the itinerary auto-select feature.
     * Held outside the cache because {@link RealtimeCache.applySnapshot} clears
     * the delay map on every poll; overrides must survive until explicitly reset.
     */
    private readonly delayOverrides = new Map<string, TripDelayWire>();

    private interpolationEngine: InterpolationEngine = new InterpolationEngine();

    /** Last seen timeline reference per trip (detect cache rebuild without POJO copy). */
    private readonly lastTimelineByTripId = new Map<string, CompactTimeline>();

    /** Last seen schedule object per trip (reference equality). */
    private readonly lastScheduleByTripId = new Map<string, TripScheduleWire>();

    /**
     * Trip ids present after the last successful cache sync.
     * Used by cleanup to avoid pruning buses during transient gaps (legacy `lastFetchedIds`).
     * Do not remove: aggressive moveend cleanup without this guard caused bus flicker rollback.
     */
    private lastActiveTripIds = new Set<string>();

    constructor() {
    // Initialize Maps
        this.vehicles = new Map();
        this.colorCache = new Map();
        this.typeCache = new Map();
    }

    /**
     * Bind a RealtimeCache for the new polling-based system.
     *
     * @param cache Realtime cache used as the source of truth for trip data.
     * @returns Nothing.
     */
    setRealtimeCache( cache: RealtimeCache ): void {
        this.realtimeCache = cache;
    }

    /**
     * Returns the currently bound realtime cache instance, if any.
     *
     * @returns Bound realtime cache, or null when not configured.
     */
    getRealtimeCache(): RealtimeCache | null {
        return this.realtimeCache;
    }

    /**
     * Sets or clears the itinerary-driven delay override for one trip.
     *
     * @param tripId Trip identifier.
     * @param delay Delay payload to apply, or `null` to restore the GTFS-RT delay.
     * @returns Nothing.
     */
    setDelayOverrideForTrip( tripId: string, delay: TripDelayWire | null ): void {
        if ( delay === null ) {
            this.delayOverrides.delete( tripId );
        } else {
            this.delayOverrides.set( tripId, delay );
        }
    }

    /**
     * Returns the delay applied for a trip: itinerary override when present,
     * otherwise the GTFS-RT delay from the realtime cache.
     *
     * @param tripId Trip identifier.
     * @returns Effective delay payload, or undefined when no delay is known.
     */
    getEffectiveDelay( tripId: string ): TripDelayWire | undefined {
        return this.delayOverrides.get( tripId ) ?? this.realtimeCache?.delays.get( tripId );
    }

    /**
     * Exposes internal maps for realtime memory profiling helpers.
     *
     * @returns Vehicle, cache, and interpolation handles used by diagnostics.
     */
    getRealtimeMemoryProfilingInputs(): {
        "vehicles":            Map<string, Vehicle>;
        "colorCache":          Map<string, Color>;
        "typeCache":           Map<string, VehicleType>;
        "interpolationEngine": InterpolationEngine;
    } {
        return {
            "vehicles":            this.vehicles,
            "colorCache":          this.colorCache,
            "typeCache":           this.typeCache,
            "interpolationEngine": this.interpolationEngine,
        };
    }

    /**
     * Resets all interpolation state so the next render frame re-initialises
     * each vehicle at its current delay-aware target position.
     * Call this after a long pause (tab hidden / app backgrounded) to avoid
     * stale delta accumulation in the speed curve.
     */
    resetInterpolationState(): void {
        this.interpolationEngine.clear();
    }

    /**
     * Called by PollingManager after a snapshot is applied to the cache.
     * Rebuilds Vehicle objects from cached timelines + schedules.
     *
     * @returns Nothing.
     */
    onDataUpdate(): void {
        const cache = this.realtimeCache;
        if ( !cache ) {
            return;
        }

        const activeTripIds = new Set( cache.getActiveTripIds() );

        // Remove vehicles no longer in cache
        const toRemove: string[] = [];
        for ( const id of this.vehicles.keys() ) {
            if ( !activeTripIds.has( id ) ) {
                toRemove.push( id );
            }
        }
        for ( const id of toRemove ) {
            this.removeVehicleEntry( id );
        }

        // Create or update vehicles from cache
        for ( const tripId of activeTripIds ) {
            const scheduleEntry = cache.schedules.get( tripId );
            const timeline = cache.timelines.get( tripId );
            if ( !scheduleEntry || !timeline || timeline.length < 2 ) {
                continue;
            }

            const schedule = scheduleEntry.data;
            const existing = this.vehicles.get( tripId );
            if ( existing ) {
                const prevTimeline = this.lastTimelineByTripId.get( tripId );
                const prevSchedule = this.lastScheduleByTripId.get( tripId );
                if ( prevTimeline !== timeline || prevSchedule !== schedule ) {
                    existing.lastUpdateTime = Date.now();
                }
                this.lastTimelineByTripId.set( tripId, timeline );
                this.lastScheduleByTripId.set( tripId, schedule );
                existing.color = {
                    "r": schedule.route_color[0],
                    "g": schedule.route_color[1],
                    "b": schedule.route_color[2],
                };
                existing.label = schedule.route_short_name;
                existing.type = this.getVehicleType( schedule.route_type );
            } else {
                this.createVehicleFromSchedule( tripId, schedule, timeline );
            }
        }

        this.lastActiveTripIds = new Set( activeTripIds );
    }

    /**
     * Removes a vehicle and all associated interpolation / tracking state.
     *
     * @param tripId Trip identifier to remove.
     * @returns Nothing.
     */
    private removeVehicleEntry( tripId: string ): void {
        this.vehicles.delete( tripId );
        this.interpolationEngine.removeVehicle( tripId );
        this.lastTimelineByTripId.delete( tripId );
        this.lastScheduleByTripId.delete( tripId );
    }

    /**
     * Get the position for a vehicle using the new InterpolationEngine.
     * @param tripId Trip identifier.
     * @param timestampMs Render timestamp in milliseconds.
     * @returns Interpolated realtime position, or null if no data is available.
     */
    getRealtimePosition( tripId: string, timestampMs: number ): VehicleRenderPosition | null {
        const cache = this.realtimeCache;
        if ( !cache ) {
            return null;
        }

        const timeline = cache.timelines.get( tripId );
        const schedule = cache.schedules.get( tripId );
        const delay = this.getEffectiveDelay( tripId );

        if ( !timeline || !schedule ) {
            return null;
        }

        return this.interpolationEngine.getPosition(
            tripId,
            timeline,
            delay,
            schedule.data.stops,
            timestampMs,
        );
    }

    /**
     * Columnar fallback leading coordinate for map path overlays when
     * {@link getRealtimePosition} returns null (clamped scheduled time, no POJO copy).
     *
     * @param tripId Trip identifier.
     * @param timestampMs Reference timestamp in milliseconds.
     * @returns Latitude/longitude or null.
     */
    getTimelineLeadingCoordAtMs( tripId: string, timestampMs: number ): {
        "lat": number;
        "lon": number;
    } | null {
        const cache = this.realtimeCache;
        if ( !cache ) {
            return null;
        }

        const timeline = cache.timelines.get( tripId );
        const schedule = cache.schedules.get( tripId );
        if ( !timeline || !schedule ) {
            return null;
        }

        return getTimelineLeadingCoordAtMs( {
            timeline,
            "delay":               this.getEffectiveDelay( tripId ),
            "stops":               schedule.data.stops,
            timestampMs,
            "interpolationEngine": this.interpolationEngine,
        } );
    }

    /**
     * Creates and stores a vehicle instance from cached schedule and timeline data.
     *
     * @param tripId Trip identifier.
     * @param schedule Cached GTFS schedule payload.
     * @param timeline Timeline points derived from shape + schedule.
     * @returns Nothing.
     */
    private createVehicleFromSchedule(
        tripId: string,
        schedule: TripScheduleWire,
        timeline: CompactTimeline,
    ): void {
        const vehicleType = this.getVehicleType( schedule.route_type );
        const newVehicle = new Vehicle( tripId, vehicleType, schedule.route_short_name );
        newVehicle.color = {
            "r": schedule.route_color[0],
            "g": schedule.route_color[1],
            "b": schedule.route_color[2],
        };
        this.vehicles.set( tripId, newVehicle );
        this.lastTimelineByTripId.set( tripId, timeline );
        this.lastScheduleByTripId.set( tripId, schedule );
    }

    /*
     * ---------------------------------------------------------------------
     * Set the map on which vehicles will be rendered
     * ---------------------------------------------------------------------
     */
    setMap( map: maplibregl.Map ) {
        this.map = map;

        // Start periodic cleanup every 60 seconds
        if ( !this.cleanupInterval ) {
            this.cleanupInterval = setInterval( () => {
                this.cleanupOrphanVehicles();
            }, VEHICLE.cleanupIntervalMs );
            console.log( "[VehicleManager] Periodic cleanup started (60s interval)" );
        }

        // Trigger cleanup after map movements (zoom or pan)
        this.map.on( "moveend", () => {
            this.cleanupOrphanVehicles();
        } );

        this.map.on( "zoomend", () => {
            this.cleanupOrphanVehicles();
        } );
    }

    getMap() {
        return this.map;
    }

    /*
     * ---------------------------------------------------------------------
     * Add a Vehicle to the vehicles Map
     * ---------------------------------------------------------------------
     */
    addVehicle( vehicle: Vehicle ) {
        this.vehicles.set( vehicle.id, vehicle );
    }

    /*
     * ---------------------------------------------------------------------
     * Remove a Vehicle from the Map
     * ---------------------------------------------------------------------
     */
    removeVehicle( vehicleId: string ) {
        this.removeVehicleEntry( vehicleId );
    }

    /*
     * ---------------------------------------------------------------------
     * Returns a specific Vehicle, if present
     * ---------------------------------------------------------------------
     */
    getVehicle( vehicleId: string ): Vehicle | undefined {
        return this.vehicles.get( vehicleId );
    }

    /*
     * ---------------------------------------------------------------------
     * Retourne la liste de tous les Vehicles
     * ---------------------------------------------------------------------
     */
    getAllVehicles(): Vehicle[] {
        return Array.from( this.vehicles.values() );
    }

    private routeVisualizationEnabled: boolean = false;

    /** Periodic cleanup interval for orphan vehicles */
    private cleanupInterval: NodeJS.Timeout | null = null;

    setVisible( visible: boolean ) {
        this.visible = visible;
    }

    isVisible() {
        return this.visible;
    }

    /*
     * ---------------------------------------------------------------------
     * Converts a hexadecimal string "rrggbb" to {r, g, b}.
     * Uses a cache to avoid re-conversion each time.
     * ---------------------------------------------------------------------
     */
    private parseColor( colorStr: string | null ): Color {
        if ( colorStr === "" || colorStr === undefined || colorStr === null ) {
            // Default color if string is empty or null
            return {
                "r": 255,
                "g": 0,
                "b": 0,
            };
        }

        // Check if we already stored the color in cache
        const cached = this.colorCache.get( colorStr );
        if ( cached !== undefined ) {
            return cached;
        }

        // Calcule la couleur et la stocke
        const color = {
            "r": parseInt( colorStr.slice( 0, 2 ), 16 ),
            "g": parseInt( colorStr.slice( 2, 4 ), 16 ),
            "b": parseInt( colorStr.slice( 4, 6 ), 16 ),
        };
        this.colorCache.set( colorStr, color );

        return color;
    }

    /*
     * ---------------------------------------------------------------------
     * Convertit un route_type (ex: "700", "3") en VehicleType (Bus, Tram,...)
     * Uses internal cache to avoid repetition
     * Extended type definitions: https://developers.google.com/transit/gtfs/reference/extended-route-types
     * ---------------------------------------------------------------------
     */
    private getVehicleType( routeType: string | number ): VehicleType {
        const routeTypeStr = String( routeType );
        const cached = this.typeCache.get( routeTypeStr );
        if ( cached !== undefined ) {
            return cached;
        }

        let type: VehicleType;

        if ( isNaN( Number( routeTypeStr ) ) ) {
            type = VehicleType.Bus;
        } else {
            const routeTypeNum = Number( routeTypeStr );

            switch ( true ) {
                case routeTypeNum === 0:
                    type = VehicleType.Tram;
                    break;

                case routeTypeNum === 1:
                    type = VehicleType.UrbanRailway;
                    break;

                case routeTypeNum === 2:
                    type = VehicleType.Train;
                    break;

                case routeTypeNum === 3:
                    type = VehicleType.Bus;
                    break;

                case routeTypeNum === 4:
                    type = VehicleType.Ferry;
                    break;

                case routeTypeNum === 5:
                    type = VehicleType.Tram;
                    break;

                case routeTypeNum === 6: // AerialLift: We do not support this type yet
                    type = VehicleType.Bus;
                    break;

                case routeTypeNum === 7:
                    type = VehicleType.Funicular;
                    break;

                case routeTypeNum === 11:
                    type = VehicleType.Bus;
                    break;

                case routeTypeNum === 12:
                    type = VehicleType.Tram;
                    break;

                case routeTypeNum < 200:
                    type = VehicleType.Train;
                    break;

                case routeTypeNum < 300:
                    type = VehicleType.Car;
                    break;

                case routeTypeNum < 400: // Not defined on the standard, fallback to bus
                    type = VehicleType.Bus;
                    break;

                case routeTypeNum < 500:
                    type = VehicleType.UrbanRailway;
                    break;

                case routeTypeNum < 700: // 500-699: Not defined on the standard, fallback to bus
                    type = VehicleType.Bus;
                    break;

                case routeTypeNum < 800:
                    type = VehicleType.Bus;
                    break;

                case routeTypeNum < 900: // Trolleybus
                    type = VehicleType.Bus;
                    break;

                case routeTypeNum < 1000:
                    type = VehicleType.Tram;
                    break;

                case routeTypeNum < 1100:
                    type = VehicleType.Water;
                    break;

                case routeTypeNum < 1200: // AirService: We do not support this type yet
                    type = VehicleType.Bus;
                    break;

                case routeTypeNum < 1300:
                    type = VehicleType.Ferry;
                    break;

                case routeTypeNum < 1400: // AerialLift: We do not support this type yet
                    type = VehicleType.Bus;
                    break;

                case routeTypeNum < 1500:
                    type = VehicleType.Funicular;
                    break;

                case routeTypeNum < 1600:
                    type = VehicleType.Taxi;
                    break;

                default:
                    type = VehicleType.Bus;
            }
        }
        this.typeCache.set( routeTypeStr, type );

        return type;
    }

    /*
     * ---------------------------------------------------------------------
     * Periodic cleanup of orphan vehicles
     * Called every 60 seconds to ensure no vehicles get stuck
     * ---------------------------------------------------------------------
     */
    private cleanupOrphanVehicles() {
        const now = Date.now();
        const vehiclesToRemove: string[] = [];
        let staleRemovals = 0;
        let expiredRemovals = 0;

        for ( const [ id, vehicle ] of this.vehicles ) {
            // Skip trips from the latest cache sync (anti-flicker; do not optimize away).
            if ( this.lastActiveTripIds.has( id ) ) {
                continue;
            }

            // Remove if not updated in 5 minutes
            if ( now - vehicle.lastUpdateTime > VEHICLE.staleUpdateMs ) {
                console.log( "[Cleanup] Removing stale vehicle", {
                    id,
                    "lastUpdate": new Date( vehicle.lastUpdateTime ).toISOString(),
                } );
                vehiclesToRemove.push( id );
                staleRemovals += 1;

                continue;
            }

            const position = this.getRealtimePosition( id, now );
            if ( position === null ) {
                const timeline = this.realtimeCache?.timelines.get( id );
                const timeWindow = timeline !== undefined ? timelineEpochTimeWindowMs( timeline ) : null;
                if ( timeWindow !== null && now > timeWindow.end + VEHICLE.positionGraceMs ) {
                    vehiclesToRemove.push( id );
                    expiredRemovals += 1;
                }
            }
        }

        for ( const id of vehiclesToRemove ) {
            this.removeVehicleEntry( id );
        }

        if ( vehiclesToRemove.length > 0 ) {
            console.log( "[Cleanup] Summary", {
                "removedTotal": vehiclesToRemove.length,
                staleRemovals,
                expiredRemovals,
                "remaining":    this.vehicles.size,
            } );
        }
    }

    /**
     * Clean up resources (call when destroying the manager)
     *
     * /**
     * Clean up resources (call when destroying the manager)
     */
    destroy() {
        if ( this.cleanupInterval ) {
            clearInterval( this.cleanupInterval );
            this.cleanupInterval = null;
            console.log( "[VehicleManager] Periodic cleanup stopped" );
        }
    }

    /**
     * Visualizes each vehicle's route shapes on the map (dev Layers → Bus shapes).
     *
     * @param singleVehicle When set, only this vehicle's shape is drawn.
     */
    public visualizeTrips( singleVehicle?: Vehicle ): void {
        if ( this.map === undefined ) {
            console.warn( "[VehicleManager] Aucune carte définie. Appelez setMap(map)." );

            return;
        }

        this.routeVisualizationEnabled = true;

        syncBusRouteShapesOnMap( this.map, {
            "vehicles":        this.vehicles.values(),
            "getTimeline":     ( vehicleId ) => this.realtimeCache?.timelines.get( vehicleId ),
            "singleVehicleId": singleVehicle?.id,
        } );
    }

    /**
     * Re-adds route visualisation layers after a map style change.
     * Only acts when route visualisation was previously enabled.
     */
    public reAddRouteLayers(): void {
        if ( this.routeVisualizationEnabled ) {
            this.visualizeTrips();
        }
    }

    /*
     * ---------------------------------------------------------------------
     * Clears all vehicle routes from the map
     * ---------------------------------------------------------------------
     */
    public clearTrips(): void {
        this.routeVisualizationEnabled = false;
        removeBusRouteShapesFromMap( this.map );
    }

}
